/**
 * DailyCheckinCard — iter1054
 * Един вход за три близнака: настроение, енергия, стрес и фокус.
 * Показва се на страниците на стрес, психо-емоционален и когнитивен
 * близнак — един и същи запис, едно питане на ден.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DailyCheckinCard = ({ isDark, onSaved }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vals, setVals] = useState({});
  const [note, setNote] = useState("");

  const apply = (data) => {
    setD(data);
    const t = data.today || {};
    setVals({
      mood_1_10: t.mood_1_10 ?? 5,
      energy_1_10: t.energy_1_10 ?? 5,
      stress_1_10: t.stress_1_10 ?? 5,
      focus_1_10: t.focus_1_10 ?? 5,
    });
    setNote(t.note_bg || "");
  };

  useEffect(() => {
    axios.get(`${API}/daily-checkin`, { timeout: 25000 })
      .then(({ data }) => apply(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/daily-checkin`, { ...vals, note_bg: note });
      apply(data);
      onSaved && onSaved(data);
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  if (loading) {
    return <div className={`p-4 rounded-2xl border ${card}`} data-testid="daily-checkin-loading">
      <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
    </div>;
  }
  if (!d) return null;
  const box = isDark ? "bg-slate-800/40" : "bg-slate-50 border border-slate-200";

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="daily-checkin-card">
      <div className="flex items-center gap-2 mb-1">
        {d.done_today
          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          : <Flame className="w-4 h-4 text-violet-500" />}
        <h2 className="font-bold text-sm">{d.title_bg}</h2>
        <span className="ml-auto text-[10px] font-extrabold tabular-nums" data-testid="daily-checkin-streak">
          {d.streak} {d.streak === 1 ? "ден" : "дни"} поред
        </span>
      </div>
      <p className="text-[11px] opacity-70 mb-3" data-testid="daily-checkin-summary">{d.summary_bg}</p>

      <div className="space-y-2.5">
        {(d.fields || []).map((f) => (
          <div key={f.key} data-testid={`daily-field-${f.key}`}>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="font-semibold">{f.label_bg}</span>
              <span className="ml-auto font-extrabold tabular-nums" data-testid={`daily-value-${f.key}`}>
                {vals[f.key]}
              </span>
              {d.week_avg && d.week_avg[f.key] != null && (
                <span className="opacity-50 text-[10px]">7 дни: {d.week_avg[f.key]}</span>
              )}
            </div>
            <input type="range" min="1" max="10" step="1" value={vals[f.key] ?? 5}
                   onChange={(e) => setVals((v) => ({ ...v, [f.key]: Number(e.target.value) }))}
                   data-testid={`daily-slider-${f.key}`}
                   className="w-full mt-1 accent-violet-600" />
            <div className="flex justify-between text-[9px] opacity-60">
              <span>{f.low_bg}</span><span>{f.high_bg}</span>
            </div>
            <div className="text-[9px] opacity-60 italic">{f.why_bg}</div>
          </div>
        ))}
      </div>

      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Бележка (по желание)" data-testid="daily-note"
                className={`w-full mt-2 px-2 py-1.5 rounded-lg text-[11px] ${
                  isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                }`} />

      <button onClick={save} disabled={saving} data-testid="daily-checkin-save"
              className="mt-2 w-full py-2.5 rounded-xl bg-violet-600 text-white text-[11px] font-bold disabled:opacity-60 hover:bg-violet-700 transition-colors">
        {saving ? "Записвам…" : d.done_today ? "Обнови днешното отбелязване" : "Отбележи за днес"}
      </button>

      <div className={`mt-3 p-2 rounded-lg text-[10px] ${box}`} data-testid="daily-targets">
        <div className="font-bold opacity-80 mb-1">Едно отбелязване, три близнака</div>
        {(d.targets || []).map((t) => (
          <button key={t.twin} onClick={() => navigate(t.route)}
                  data-testid={`daily-target-${t.twin}`}
                  className={`w-full text-left p-1.5 rounded-lg mb-1 transition-colors ${
                    isDark ? "hover:bg-slate-700/50" : "hover:bg-white"
                  }`}>
            <span className="font-bold">{t.title_bg} →</span>
            <span className="opacity-80"> {t.text_bg}</span>
          </button>
        ))}
        <div className="italic opacity-70 mt-0.5">{d.note_bg}</div>
      </div>

      {(d.history || []).length > 1 && (
        <div className={`mt-2 p-2 rounded-lg text-[10px] ${box}`} data-testid="daily-history">
          <div className="font-bold opacity-80 mb-1">Последни дни</div>
          {d.history.map((h) => (
            <div key={h.logged_date} className="flex gap-2" data-testid={`daily-history-${h.logged_date}`}>
              <span className="opacity-60">{h.logged_date}</span>
              <span>настроение {h.mood_1_10}</span>
              <span>енергия {h.energy_1_10}</span>
              <span>стрес {h.stress_1_10}</span>
              <span>фокус {h.focus_1_10}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyCheckinCard;
